
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { EventVoting } from "@/components/EventVoting";
import { EventRSVP } from "@/components/EventRSVP";
import { EventResults } from "@/components/EventResults";
import { EventSubscription } from "@/components/EventSubscription";
import { EventComments } from "@/components/EventComments";
import { eventService } from "@/services/eventService";
import { Event } from "@/types/event";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import NavigationBar from "@/components/NavigationBar";
import { ArrowLeft } from "lucide-react";
import { analyticsService } from "@/services/analyticsService";

const Vote = () => {
  const { shareableLink } = useParams<{ shareableLink: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<'vote' | 'results'>('vote');
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const loadEvent = async () => {
      if (!shareableLink) {
        toast({
          title: "Invalid link",
          description: "The event link is invalid.",
          variant: "destructive",
        });
        return;
      }

      // Wait for auth to load
      if (authLoading) {
        return;
      }

      try {
        console.log('Loading event by shareable link:', shareableLink);
        const eventData = await eventService.getEventByShareableLink(shareableLink);
        
        if (!eventData) {
          toast({
            title: "Event not found",
            description: "The event you're looking for doesn't exist or has been removed.",
            variant: "destructive",
          });
          return;
        }

        console.log('Event loaded:', eventData);
        console.log('Event guests:', eventData.guests);
        console.log('Current user:', user);
        console.log('User email:', user?.email);

        // Check authorization - event should be accessible to authenticated users or guests
        let isAuthorized = false;
        
        if (user) {
          // For authenticated users, check if they're the host or invited guest
          const userEmail = user.email;
          const isEventHost = eventData.userId === user.id;
          const isGuestInvited = eventData.guests.some(guest => {
            console.log('Checking guest:', guest, 'against user email:', userEmail);
            return guest.email === userEmail;
          });
          
          console.log('Authorization check:', {
            userEmail,
            isEventHost,
            isGuestInvited,
            guestEmails: eventData.guests.map(g => g.email)
          });
          
          isAuthorized = isEventHost || isGuestInvited;
        } else {
          // For non-authenticated users, show login prompt for active events
          isAuthorized = false;
        }
        
        if (!isAuthorized) {
          setIsAuthorized(false);
          setEvent(eventData); // Set event to show the login prompt
          setLoading(false);
          return;
        }

        console.log('Event loaded and user authorized:', eventData);
        setEvent(eventData);
        setIsAuthorized(true);

        // Track event view (only if user is authenticated)
        if (user) {
          const currentGuest = eventData.guests.find(guest => guest.email === user.email);
          analyticsService.trackEventView(eventData.id, user.id, currentGuest?.id);
        }
      } catch (error) {
        console.error('Error loading event:', error);
        toast({
          title: "Error",
          description: "Failed to load event. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [shareableLink, toast, user, authLoading, navigate]);

  const handleEventUpdate = (updatedEvent: Event) => {
    console.log('Updating event from real-time subscription:', updatedEvent);
    setEvent(updatedEvent);
  };

  const handleVoteSubmitted = () => {
    setCurrentView('results');
    toast({
      title: "Vote submitted!",
      description: "Thank you for voting. You can see the current results below.",
    });
  };

  // Check if current user has already voted (for voting events)
  const hasCurrentUserVoted = () => {
    if (!user?.email || !event) return false;
    const currentGuest = event.guests.find(g => g.email === user.email);
    return currentGuest?.hasVoted || false;
  };

  // Determine which view to show
  const getViewToShow = () => {
    if (currentView === 'results') return 'results';
    if (event?.eventType === 'voting' && hasCurrentUserVoted()) return 'results';
    return 'vote';
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading || authLoading) {
    return (
      <>
        <NavigationBar />
        <div className="min-h-screen bg-gradient-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading event...</p>
          </div>
        </div>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <NavigationBar />
        <div className="min-h-screen bg-gradient-background flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Event Not Found</h1>
            <p className="text-muted-foreground">
              The event you're looking for doesn't exist or has been removed.
            </p>
            <Button 
              onClick={() => navigate('/')} 
              className="mt-4"
            >
              Go to Home
            </Button>
          </div>
        </div>
      </>
    );
  }

  if (!isAuthorized) {
    return (
      <>
        <NavigationBar />
        <div className="min-h-screen bg-gradient-background flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            {!user ? (
              // Show login prompt for unauthenticated users
              <>
                <h1 className="text-2xl font-bold mb-4">Sign In to Vote</h1>
                <p className="text-muted-foreground mb-4">
                  Please sign in to vote on this event. You must be logged in with the email address that received the invitation.
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Event: <strong>{event.name}</strong> by {event.hostName}
                </p>
                <div className="space-y-2">
                  <Button 
                    onClick={() => navigate('/auth')} 
                    className="w-full"
                  >
                    Sign In to Vote
                  </Button>
                  <Button 
                    onClick={() => navigate('/')} 
                    variant="outline"
                    className="w-full"
                  >
                    Go to Home
                  </Button>
                </div>
              </>
            ) : (
              // Show access denied for authenticated but not invited users
              <>
                <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
                <p className="text-muted-foreground mb-4">
                  You are not invited to this event. Only guests who received an invitation can vote.
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Event: <strong>{event.name}</strong> by {event.hostName}
                </p>
                <div className="space-y-2">
                  <Button 
                    onClick={() => navigate('/')} 
                    className="w-full"
                  >
                    Go to Home
                  </Button>
                  <Button 
                    onClick={() => navigate('/auth')} 
                    variant="outline"
                    className="w-full"
                  >
                    Sign in with different account
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <NavigationBar />
      {/* Real-time subscription component */}
      <EventSubscription 
        eventId={event.id} 
        onEventUpdate={handleEventUpdate} 
      />
      
      <div className="min-h-screen bg-gradient-background">
        <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
          <Button 
            variant="ghost" 
            onClick={handleBackToDashboard} 
            className="mb-4 sm:mb-6 flex items-center gap-2 touch-manipulation"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
          
          <Tabs defaultValue="event" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-auto">
              <TabsTrigger value="event" className="text-sm sm:text-base py-2 sm:py-2.5">
                {getViewToShow() === 'vote' ? 'Vote' : 'Results'}
              </TabsTrigger>
              <TabsTrigger value="discussion" className="text-sm sm:text-base py-2 sm:py-2.5">Discussion</TabsTrigger>
            </TabsList>
            
            <TabsContent value="event" className="mt-4 sm:mt-6">
              {getViewToShow() === 'vote' ? (
                event.eventType === 'rsvp' ? (
                  <EventRSVP
                    event={event}
                    onRSVPSubmitted={handleVoteSubmitted}
                  />
                ) : (
                  <EventVoting
                    event={event}
                    onVoteSubmitted={handleVoteSubmitted}
                    onBack={handleBackToDashboard}
                    authenticatedUser={user}
                  />
                )
              ) : (
                <EventResults
                  event={event}
                  onBack={() => setCurrentView('vote')}
                />
              )}
            </TabsContent>
            
            <TabsContent value="discussion" className="mt-4 sm:mt-6">
              <EventComments
                eventId={event.id}
                isEventOwner={event.userId === user?.id}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default Vote;
